import { useEffect, useRef, useState } from 'react';

import { useFilterStore } from './filterStore';
import { useScopeContracts } from '../../services/api/hooks/useScopeContracts';
import { useScopeEntities } from '../../services/api/hooks/useScopeEntities';
import { useScopeSites } from '../../services/api/hooks/useScopeSites';
import { IconCheck, IconChevronDown, IconX } from '../incidents/icons';

/**
 * Tant que `/v1/extranet/scope/{entities,contracts,sites}` n'est pas livré côté back,
 * on masque les 3 MultiSelect scope. Le filtre Période reste actif et toutes les
 * routes back (perf, trips, incidents, invoices) acceptent entityIds/contractIds/siteIds
 * vides. Flip à `true` dès que Yohan livre les 3 routes scope.
 */
const SCOPE_FILTERS_ENABLED = false;

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

const toInputValue = (iso: string) => iso.slice(0, 10);
const fromInputValue = (date: string) => new Date(`${date}T00:00:00Z`).toISOString();

const formatRange = (from: string, to: string) => {
  const f = new Date(from);
  const t = new Date(to);
  const fMonth = f.getUTCMonth();
  const tMonth = t.getUTCMonth();
  const fYear = f.getUTCFullYear();
  const tYear = t.getUTCFullYear();
  const fDate = f.getUTCDate();
  const tDate = t.getUTCDate();
  if (fMonth === tMonth && fYear === tYear) {
    return `${fDate}–${tDate} ${MONTHS[fMonth]} ${fYear}`;
  }
  return `${fDate} ${MONTHS[fMonth].slice(0, 4)}. – ${tDate} ${MONTHS[tMonth].slice(0, 4)}. ${tYear}`;
};

export const GlobalFilters = () => {
  const { entityIds, contractIds, siteIds, dateRange, setEntityIds, setContractIds, setSiteIds, setDateRange, reset } =
    useFilterStore();

  const entitiesQuery = useScopeEntities();
  const contractsQuery = useScopeContracts({ entityIds });
  const sitesQuery = useScopeSites({ contractIds });

  const entities = entitiesQuery.data?.entities ?? [];
  const contracts = contractsQuery.data?.contracts ?? [];
  const sites = sitesQuery.data?.sites ?? [];

  // Cascade : si entityIds change, on retire les contracts qui ne sont plus disponibles
  useEffect(() => {
    if (!contractsQuery.data) return;
    const available = new Set(contracts.map(c => c.id));
    const filtered = contractIds.filter(id => available.has(id));
    if (filtered.length !== contractIds.length) setContractIds(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsQuery.data]);

  useEffect(() => {
    if (!sitesQuery.data) return;
    const available = new Set(sites.map(s => s.id));
    const filtered = siteIds.filter(id => available.has(id));
    if (filtered.length !== siteIds.length) setSiteIds(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitesQuery.data]);

  const hasFilter =
    entityIds.length > 0 ||
    contractIds.length > 0 ||
    siteIds.length > 0 ||
    dateRange.from.slice(0, 10) !==
      new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  return (
    <section aria-label="Filtres globaux" className="flex flex-wrap items-center gap-2">
      {SCOPE_FILTERS_ENABLED ? (
        <>
          <MultiSelect
            label="Entité"
            placeholder="Toutes les entités"
            options={entities.map(e => ({ value: e.id, label: e.name }))}
            value={entityIds}
            onChange={setEntityIds}
            isLoading={entitiesQuery.isLoading}
          />

          <MultiSelect
            label="Contrat"
            placeholder={entityIds.length > 0 ? `Tous les contrats (${contracts.length})` : 'Tous les contrats'}
            options={contracts.map(c => ({ value: c.id, label: c.label, mono: true }))}
            value={contractIds}
            onChange={setContractIds}
            isLoading={contractsQuery.isLoading}
          />

          <MultiSelect
            label="Établissement"
            placeholder={contractIds.length > 0 ? `Tous (${sites.length})` : 'Tous les établissements'}
            options={sites.map(s => ({ value: s.id, label: s.name, sublabel: s.city }))}
            value={siteIds}
            onChange={setSiteIds}
            isLoading={sitesQuery.isLoading}
          />
        </>
      ) : null}

      <DateRangeButton from={dateRange.from} to={dateRange.to} onChange={setDateRange} />

      {hasFilter ? (
        <button
          type="button"
          onClick={reset}
          className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors"
        >
          Réinitialiser
        </button>
      ) : null}
    </section>
  );
};

interface MultiSelectProps {
  label: string;
  placeholder: string;
  options: Array<{ value: string; label: string; sublabel?: string; mono?: boolean }>;
  value: string[];
  onChange: (next: string[]) => void;
  isLoading?: boolean;
}

const MultiSelect = ({ label, placeholder, options, value, onChange, isLoading }: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const toggle = (val: string) => {
    onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);
  };

  const trigger = (() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const sel = options.find(o => o.value === value[0]);
      return sel?.label ?? placeholder;
    }
    return `${label} : ${value.length}`;
  })();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={[
          'inline-flex items-center gap-2 h-8 px-3 text-sm rounded-md border transition-colors duration-100',
          value.length > 0
            ? 'bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800'
            : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50',
        ].join(' ')}
      >
        <span className={value.length > 0 ? 'text-white' : 'text-zinc-500'}>{label}</span>
        <span className="text-xs">{value.length > 0 ? trigger : placeholder}</span>
        <IconChevronDown width={12} height={12} className={value.length > 0 ? 'text-white/70' : 'text-zinc-400'} />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-72 bg-white border border-zinc-200 rounded-md shadow-md py-1.5 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-6 text-sm text-zinc-500 text-center">Chargement…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-6 text-sm text-zinc-500 text-center">
              Aucune option disponible sur le périmètre actuel.
            </div>
          ) : (
            <>
              {value.length > 0 ? (
                <div className="px-1.5 pb-1.5 border-b border-zinc-100">
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="w-full h-8 px-2 flex items-center gap-2 rounded text-xs text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors text-left"
                  >
                    <IconX width={12} height={12} />
                    <span>Tout effacer ({value.length})</span>
                  </button>
                </div>
              ) : null}
              <ul className="flex flex-col px-1.5 pt-1">
                {options.map(opt => {
                  const checked = value.includes(opt.value);
                  return (
                    <li key={opt.value}>
                      <button
                        type="button"
                        onClick={() => toggle(opt.value)}
                        className="w-full min-h-[34px] px-2 py-1.5 flex items-center gap-2 rounded text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left"
                      >
                        <span
                          className={[
                            'h-3.5 w-3.5 rounded-sm border inline-flex items-center justify-center shrink-0',
                            checked ? 'bg-zinc-900 border-zinc-900 text-white' : 'border-zinc-300 bg-white',
                          ].join(' ')}
                        >
                          {checked ? <IconCheck width={10} height={10} strokeWidth={3} /> : null}
                        </span>
                        <span className="flex-1 flex flex-col min-w-0">
                          <span className={['truncate', opt.mono ? 'font-mono text-xs tabular-nums' : ''].join(' ')}>
                            {opt.label}
                          </span>
                          {opt.sublabel ? <span className="text-xs text-zinc-500">{opt.sublabel}</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

const DateRangeButton = ({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const applyPreset = (days: number) => {
    const now = new Date();
    const fromDate = new Date(now.getTime() - (days - 1) * 86_400_000);
    fromDate.setUTCHours(0, 0, 0, 0);
    onChange({ from: fromDate.toISOString(), to: now.toISOString() });
    setOpen(false);
  };

  const applyMonthOffset = (offset: number) => {
    const now = new Date();
    const f = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1, 0, 0, 0, 0));
    const t = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0, 23, 59, 59, 999));
    onChange({ from: f.toISOString(), to: t.toISOString() });
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 h-8 px-3 text-sm rounded-md border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50 transition-colors duration-100"
      >
        <span className="text-zinc-500">Période</span>
        <span className="font-mono tabular-nums text-xs">{formatRange(from, to)}</span>
        <IconChevronDown width={12} height={12} className="text-zinc-400" />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-80 bg-white border border-zinc-200 rounded-md shadow-md p-3 flex flex-col gap-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-1.5">Raccourcis</p>
            <div className="grid grid-cols-2 gap-1.5">
              <PresetButton onClick={() => applyPreset(7)}>7 derniers jours</PresetButton>
              <PresetButton onClick={() => applyPreset(30)}>30 derniers jours</PresetButton>
              <PresetButton onClick={() => applyMonthOffset(0)}>Mois en cours</PresetButton>
              <PresetButton onClick={() => applyMonthOffset(-1)}>Mois précédent</PresetButton>
              <PresetButton onClick={() => applyPreset(90)}>3 derniers mois</PresetButton>
              <PresetButton onClick={() => applyPreset(365)}>12 derniers mois</PresetButton>
            </div>
          </div>

          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-2">Période personnalisée</p>
            <div className="grid grid-cols-2 gap-2">
              <DateField
                label="Du"
                value={toInputValue(from)}
                onChange={v => onChange({ from: fromInputValue(v), to })}
                max={toInputValue(to)}
              />
              <DateField
                label="Au"
                value={toInputValue(to)}
                onChange={v => onChange({ from, to: fromInputValue(v) })}
                min={toInputValue(from)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PresetButton = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-8 px-2.5 text-xs text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50 hover:border-zinc-300 transition-colors text-left"
  >
    {children}
  </button>
);

const DateField = ({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
    <input
      type="date"
      value={value}
      min={min}
      max={max}
      onChange={e => onChange(e.target.value)}
      className="h-8 px-2 text-sm border border-zinc-300 rounded text-zinc-900 font-mono tabular-nums focus:outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-900/10"
    />
  </label>
);
